import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Shield, Save, LogOut, RefreshCw, Trash2, CheckCircle2, Plus, KeyRound, Copy, MessageCircle, RotateCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { RATE_PERIODS, CATEGORY_DISPLAY } from "../lib/mdmData";
import { academicYearForDate, listAcademicYears } from "../lib/academicYear";
import { api, auth } from "../lib/api";

const YEARS = listAcademicYears(2020, 2030);
const CATEGORIES = ["balvatika", "primary", "upper_primary", "high_school"];

// Master list of all possible items across periods so admin can edit even items not present in base period
const ALL_ITEMS = ["Rice", "Dal", "Soya", "Oil", "Vegetables", "Egg", "Laddoo", "Condiment & Salt", "Fuel"];

// ------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------
function findBasePeriodForYear(ayear) {
  const [y] = ayear.split("-").map(Number);
  const midDate = new Date(y, 8, 15); // Sept 15 = solidly inside the academic year
  for (const p of RATE_PERIODS) {
    if (midDate >= p.effective_from && (!p.effective_to || midDate <= p.effective_to)) return p;
  }
  return RATE_PERIODS[RATE_PERIODS.length - 1];
}

function buildDefaultRates(ayear) {
  const base = findBasePeriodForYear(ayear);
  const out = {};
  for (const cat of CATEGORIES) {
    const catData = JSON.parse(JSON.stringify(base.rates[cat]));
    out[cat] = {
      target_total: catData.target_total ?? null,
      items: {},
    };
    for (const itemName of ALL_ITEMS) {
      out[cat].items[itemName] = catData.items[itemName]
        ? { ...catData.items[itemName] }
        : { qty_per_student: 0, unit: "unit", rate_per_student: 0, emoji: "•", auto_balance: false };
    }
  }
  return out;
}

// ------------------------------------------------------------------------
// Component
// ------------------------------------------------------------------------
export default function Admin() {
  const [authed, setAuthed] = useState(auth.isAuthed());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    if (!username || !password) return toast.error("Enter username and password");
    setLoading(true);
    try {
      const r = await api.adminLogin(username.trim(), password);
      auth.setToken(r.token);
      setAuthed(true);
      toast.success("Signed in as admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    auth.clear();
    setAuthed(false);
    setUsername("");
    setPassword("");
    toast.info("Signed out");
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="admin-login-page">
        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <div className="h-11 w-11 rounded-lg bg-emerald-700 grid place-items-center text-white"><Shield size={22}/></div>
          <h1 className="mt-4 font-display font-extrabold text-2xl text-slate-900">Admin sign-in</h1>
          <p className="mt-1 text-sm text-slate-600">Enter your credentials to manage rates by academic year.</p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="username" className="text-sm font-semibold">Username</Label>
              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)}
                     className="h-12 mt-1.5" placeholder="Admin username" autoComplete="username"
                     data-testid="admin-username-input"
                     onKeyDown={(e) => e.key === "Enter" && login()}/>
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     className="h-12 mt-1.5" placeholder="••••••••" autoComplete="current-password"
                     data-testid="admin-password-input"
                     onKeyDown={(e) => e.key === "Enter" && login()}/>
            </div>
          </div>
          <Button onClick={login} disabled={loading} className="mt-4 w-full bg-emerald-700 hover:bg-emerald-800 text-white h-12" data-testid="admin-login-btn">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </div>
      </div>
    );
  }

  return <AdminDashboard onLogout={logout} />;
}

// ------------------------------------------------------------------------
// Dashboard
// ------------------------------------------------------------------------
function AdminDashboard({ onLogout }) {
  const currentYear = academicYearForDate(new Date());
  const [year, setYear] = useState(currentYear);
  const [overrides, setOverrides] = useState([]);
  const [rates, setRates] = useState(() => buildDefaultRates(currentYear));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasOverride = useMemo(() => overrides.some((o) => o.academic_year === year), [overrides, year]);

  const refreshOverrides = async () => {
    try {
      const list = await api.listRateOverrides();
      setOverrides(list);
    } catch {
      // ignore
    }
  };

  useEffect(() => { refreshOverrides(); }, []);

  // Load rates when year changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const doc = await api.getRateOverride(year);
        if (!cancelled) {
          const merged = buildDefaultRates(year);
          // Overlay saved values on top of defaults
          for (const cat of CATEGORIES) {
            if (doc.rates?.[cat]) {
              merged[cat] = {
                target_total: doc.rates[cat].target_total ?? merged[cat].target_total,
                items: { ...merged[cat].items, ...(doc.rates[cat].items || {}) },
              };
            }
          }
          setRates(merged);
        }
      } catch {
        if (!cancelled) setRates(buildDefaultRates(year));
      } finally {
        if (!cancelled) { setLoading(false); setDirty(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  const updateItem = (cat, item, field, value) => {
    setRates((prev) => ({
      ...prev,
      [cat]: {
        ...prev[cat],
        items: {
          ...prev[cat].items,
          [item]: { ...prev[cat].items[item], [field]: field === "unit" ? value : Number(value) },
        },
      },
    }));
    setDirty(true);
  };

  const updateTarget = (cat, value) => {
    setRates((prev) => ({ ...prev, [cat]: { ...prev[cat], target_total: value === "" ? null : Number(value) } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.saveRateOverride(year, rates);
      toast.success(`Saved rates for AY ${year}`);
      setDirty(false);
      refreshOverrides();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setRates(buildDefaultRates(year));
    setDirty(true);
    toast.info("Reset to defaults — click Save to persist");
  };

  const removeOverride = async () => {
    if (!window.confirm(`Delete saved rates for AY ${year}? The calculator will fall back to the built-in defaults.`)) return;
    try {
      await api.deleteRateOverride(year);
      toast.success(`Deleted override for AY ${year}`);
      setRates(buildDefaultRates(year));
      setDirty(false);
      refreshOverrides();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="chip chip-saffron"><Shield size={14}/> Admin panel</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Manage rates by academic year</h1>
          <p className="mt-1 text-slate-600">Edits are persisted to the backend and applied to every calculation for the selected year.</p>
        </div>
        <Button variant="outline" onClick={onLogout} data-testid="admin-logout-btn"><LogOut size={16}/> Sign out</Button>
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">📈 Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">👥 Users</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">💰 Payments</TabsTrigger>
          <TabsTrigger value="rates" data-testid="admin-tab-rates">📊 Rates &amp; quantities</TabsTrigger>
          <TabsTrigger value="laddoo" data-testid="admin-tab-laddoo">🧁 Laddoo</TabsTrigger>
          <TabsTrigger value="history" data-testid="admin-tab-history">🗂 Saved overrides</TabsTrigger>
          <TabsTrigger value="ads" data-testid="admin-tab-ads">📢 Ads</TabsTrigger>
          <TabsTrigger value="recharge" data-testid="admin-tab-recharge">📱 Recharge Requests</TabsTrigger>
          <TabsTrigger value="campaign" data-testid="admin-tab-campaign">🎁 Campaign</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">📊 Analytics</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">📝 Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6"><AdminOverview/></TabsContent>
        <TabsContent value="users" className="mt-6"><AdminUsers/></TabsContent>
        <TabsContent value="payments" className="mt-6"><AdminPayments/></TabsContent>
        <TabsContent value="audit" className="mt-6"><AdminAudit/></TabsContent>

        <TabsContent value="rates" className="mt-6">
          {/* Year selector */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex-1">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">📅 Academic year</Label>
              <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-3">
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger className="h-12 w-full sm:w-56" data-testid="admin-year-select"><SelectValue/></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {YEARS.map((y) => (
                      <SelectItem key={y} value={y} data-testid={`admin-year-opt-${y}`}>{y} {overrides.some(o => o.academic_year === y) ? "✓" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasOverride ? (
                  <span className="chip chip-green"><CheckCircle2 size={12}/> Custom rates saved</span>
                ) : (
                  <span className="chip chip-slate">Using built-in defaults</span>
                )}
                {loading && <span className="text-xs text-slate-500">Loading…</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetToDefaults} data-testid="admin-reset-btn"><RefreshCw size={14}/> Reset</Button>
              {hasOverride && (
                <Button variant="outline" onClick={removeOverride} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="admin-delete-btn">
                  <Trash2 size={14}/> Delete
                </Button>
              )}
              <Button onClick={save} disabled={saving || !dirty} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-save-btn">
                <Save size={14}/> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </Button>
            </div>
          </div>

          {/* Category editors */}
          <div className="mt-6 grid gap-5">
            {CATEGORIES.map((cat) => (
              <CategoryEditor
                key={cat}
                categoryKey={cat}
                rates={rates[cat]}
                onUpdateItem={updateItem}
                onUpdateTarget={updateTarget}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="laddoo" className="mt-6">
          <LaddooRecipeManager/>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <HistoryTab overrides={overrides} setYear={setYear} refresh={refreshOverrides}/>
        </TabsContent>
        <TabsContent value="ads" className="mt-6"><AdminAds/></TabsContent>
        <TabsContent value="recharge" className="mt-6"><AdminRecharge/></TabsContent>
        <TabsContent value="campaign" className="mt-6"><AdminCampaign/></TabsContent>
        <TabsContent value="analytics" className="mt-6"><AdminAnalytics/></TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------------------
// Category editor
// ------------------------------------------------------------------------
function CategoryEditor({ categoryKey, rates, onUpdateItem, onUpdateTarget }) {
  const d = CATEGORY_DISPLAY[categoryKey];
  return (
    <div className={`rounded-2xl border-2 ${d.ui.soft} overflow-hidden`} data-testid={`admin-cat-${categoryKey}`}>
      <div className={`p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${d.ui.solid}`}>
        <div>
          <div className="font-display font-bold text-lg text-white flex items-center gap-2">
            <span className="text-2xl">{d.emoji}</span> {d.name}
          </div>
          <div className="text-xs text-white/85">Per-child quantity &amp; rate. Auto-balance target is the statutory total for the category.</div>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-white/85">Target total (₹/child)</Label>
          <Input
            type="number"
            step="0.01"
            className="h-11 w-40 mt-1.5 bg-white/95 border-white/40"
            value={rates.target_total ?? ""}
            placeholder="none"
            onChange={(e) => onUpdateTarget(categoryKey, e.target.value)}
            data-testid={`admin-target-${categoryKey}`}
          />
        </div>
      </div>

      <div className="overflow-x-auto bg-white">
        <table className="w-full text-left">
          <thead className={`${d.ui.soft} border-b border-slate-200`}>
            <tr>
              <th className="p-3 text-xs font-bold uppercase text-slate-600">Item</th>
              <th className="p-3 text-xs font-bold uppercase text-slate-600">Qty / student</th>
              <th className="p-3 text-xs font-bold uppercase text-slate-600">Unit</th>
              <th className="p-3 text-xs font-bold uppercase text-slate-600">Rate / student (₹)</th>
              <th className="p-3 text-xs font-bold uppercase text-slate-600">Auto-balance</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(rates.items).map(([name, it]) => (
              <tr key={name} className="border-t border-slate-100">
                <td className="p-2.5">
                  <div className="font-semibold text-slate-900"><span className="mr-1.5">{it.emoji || "•"}</span>{name}</div>
                </td>
                <td className="p-2.5">
                  <Input type="number" step="0.01" min="0" value={it.qty_per_student} className="h-10 w-28"
                         onChange={(e) => onUpdateItem(categoryKey, name, "qty_per_student", e.target.value)}
                         data-testid={`admin-qty-${categoryKey}-${name}`}/>
                </td>
                <td className="p-2.5">
                  <Select value={it.unit} onValueChange={(v) => onUpdateItem(categoryKey, name, "unit", v)}>
                    <SelectTrigger className="h-10 w-24" data-testid={`admin-unit-${categoryKey}-${name}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="no">no</SelectItem>
                      <SelectItem value="unit">unit</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-2.5">
                  <Input type="number" step="0.01" min="0" value={it.rate_per_student} className="h-10 w-28"
                         onChange={(e) => onUpdateItem(categoryKey, name, "rate_per_student", e.target.value)}
                         data-testid={`admin-rate-${categoryKey}-${name}`}/>
                </td>
                <td className="p-2.5 text-sm text-slate-500">
                  {name === "Vegetables" ? (rates.target_total ? "⚖ Auto (May 2025+)" : "—") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------
// Laddoo Recipe Manager — full CRUD for the Nutrition Ladoo ingredients
// ------------------------------------------------------------------------
function LaddooRecipeManager() {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.getLaddooRecipe();
      setItems(Array.isArray(r?.ingredients) ? r.ingredients : []);
      setUpdatedAt(r?.updated_at || null);
      setDirty(false);
    } catch {
      toast.error("Failed to load Laddoo recipe");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const perLadooTotal = items.reduce((s, i) => s + Number(i.rate_per_ladoo || 0), 0);
  const grams = items.reduce((s, i) => s + (i.is_service ? 0 : Number(i.grams_per_ladoo || 0)), 0);

  const patch = (idx, field, val) => {
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: field === "name" || field === "emoji" ? val : (field === "is_service" ? val : Number(val)) } : it));
    setDirty(true);
  };
  const removeRow = (idx) => {
    if (items.length <= 1) return toast.error("At least one ingredient is required");
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const addRow = () => {
    setItems((prev) => [...prev, { name: "New ingredient", emoji: "•", grams_per_ladoo: 0, rate_per_ladoo: 0, is_service: false }]);
    setDirty(true);
  };
  const save = async () => {
    if (items.some((i) => !i.name?.trim())) return toast.error("Every ingredient needs a name");
    setSaving(true);
    try {
      const clean = items.map((i) => ({
        name: i.name.trim(),
        emoji: i.emoji || "",
        grams_per_ladoo: Number(i.grams_per_ladoo || 0),
        rate_per_ladoo: Number(i.rate_per_ladoo || 0),
        is_service: !!i.is_service,
      }));
      const r = await api.saveLaddooRecipe(clean);
      setUpdatedAt(r?.value?.updated_at || new Date().toISOString());
      setDirty(false);
      toast.success("Laddoo recipe saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const resetServer = async () => {
    if (!window.confirm("Reset the Laddoo recipe to built-in defaults? Any customisations will be lost.")) return;
    try {
      await api.resetLaddooRecipe();
      toast.success("Reset to defaults");
      load();
    } catch { toast.error("Reset failed"); }
  };

  return (
    <div className="rounded-2xl border border-orange-200 bg-orange-50/40" data-testid="admin-laddoo-manager">
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b border-orange-200">
        <div>
          <div className="chip chip-saffron">🧁 Nutrition Ladoo Recipe</div>
          <h3 className="mt-2 font-display font-bold text-lg text-orange-900">Manage ingredients, grams and rates</h3>
          <p className="text-sm text-orange-900/80">These values power the public <b>Ladoo Calculator</b>. Statutory per-ladoo cap is ₹3.50.</p>
          {updatedAt && <p className="text-xs text-slate-500 mt-1">Last updated: {updatedAt.replace("T", " ").slice(0, 19)}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={resetServer} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="admin-laddoo-reset-btn">
            <Trash2 size={14}/> Reset defaults
          </Button>
          <Button variant="outline" onClick={addRow} data-testid="admin-laddoo-add-btn"><Plus size={14}/> Add ingredient</Button>
          <Button onClick={save} disabled={saving || !dirty} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-laddoo-save-btn">
            <Save size={14}/> {saving ? "Saving…" : dirty ? "Save recipe" : "Saved"}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white">
        <table className="w-full text-left">
          <thead className="bg-orange-100/60 border-b border-orange-200">
            <tr>
              <th className="p-3 text-xs font-bold uppercase text-orange-900">Emoji</th>
              <th className="p-3 text-xs font-bold uppercase text-orange-900">Ingredient</th>
              <th className="p-3 text-xs font-bold uppercase text-orange-900">Grams / Ladoo</th>
              <th className="p-3 text-xs font-bold uppercase text-orange-900">Rate / Ladoo (₹)</th>
              <th className="p-3 text-xs font-bold uppercase text-orange-900">Type</th>
              <th className="p-3 text-xs font-bold uppercase text-orange-900 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && items.map((it, idx) => (
              <tr key={idx} className="border-t border-orange-100" data-testid={`admin-laddoo-row-${idx}`}>
                <td className="p-2 w-16">
                  <Input value={it.emoji || ""} maxLength={4} className="h-10 w-14 text-center"
                         onChange={(e) => patch(idx, "emoji", e.target.value)} data-testid={`admin-laddoo-emoji-${idx}`}/>
                </td>
                <td className="p-2">
                  <Input value={it.name} className="h-10" onChange={(e) => patch(idx, "name", e.target.value)}
                         data-testid={`admin-laddoo-name-${idx}`}/>
                </td>
                <td className="p-2 w-36">
                  <Input type="number" step="0.01" min="0" value={it.grams_per_ladoo}
                         disabled={!!it.is_service}
                         onChange={(e) => patch(idx, "grams_per_ladoo", e.target.value)}
                         className="h-10 w-28" data-testid={`admin-laddoo-g-${idx}`}/>
                </td>
                <td className="p-2 w-36">
                  <Input type="number" step="0.01" min="0" value={it.rate_per_ladoo}
                         onChange={(e) => patch(idx, "rate_per_ladoo", e.target.value)}
                         className="h-10 w-28" data-testid={`admin-laddoo-r-${idx}`}/>
                </td>
                <td className="p-2 w-36">
                  <Select value={it.is_service ? "service" : "ingredient"} onValueChange={(v) => patch(idx, "is_service", v === "service")}>
                    <SelectTrigger className="h-10 w-32" data-testid={`admin-laddoo-type-${idx}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ingredient">Ingredient</SelectItem>
                      <SelectItem value="service">Service / labour</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-2 text-right">
                  <Button variant="outline" size="sm" className="text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => removeRow(idx)} data-testid={`admin-laddoo-del-${idx}`}>
                    <Trash2 size={14}/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-emerald-50 border-t-2 border-emerald-200">
              <td className="p-3"></td>
              <td className="p-3 font-display font-bold text-emerald-900">Total per Ladoo</td>
              <td className="p-3 font-bold text-emerald-900">{grams.toFixed(2)} g</td>
              <td className="p-3 font-display font-extrabold text-lg text-emerald-800" data-testid="admin-laddoo-total">
                ₹ {perLadooTotal.toFixed(2)}
                {perLadooTotal > 3.501 && <span className="ml-2 text-xs text-red-700">(⚠ over ₹3.50 norm)</span>}
              </td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// (Legacy LaddooInfo removed — LaddooRecipeManager replaces it with full CRUD)


// ------------------------------------------------------------------------
// History tab (list of saved overrides)
// ------------------------------------------------------------------------
function HistoryTab({ overrides, setYear, refresh }) {
  if (overrides.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="admin-history-empty">
        No academic-year overrides saved yet. Edit a year on the Rates &amp; quantities tab and click <b>Save changes</b>.
      </div>
    );
  }
  const del = async (ay) => {
    if (!window.confirm(`Delete saved rates for AY ${ay}?`)) return;
    try { await api.deleteRateOverride(ay); toast.success("Deleted"); refresh(); } catch { toast.error("Delete failed"); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-left">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="p-3 text-xs font-bold uppercase text-slate-500">Academic year</th>
            <th className="p-3 text-xs font-bold uppercase text-slate-500">Updated</th>
            <th className="p-3 text-xs font-bold uppercase text-slate-500 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {overrides.map((o) => (
            <tr key={o.academic_year} className="border-t border-slate-100" data-testid={`history-row-${o.academic_year}`}>
              <td className="p-3 font-semibold text-slate-900">{o.academic_year}</td>
              <td className="p-3 text-sm text-slate-600">{(o.updated_at || "").replace("T", " ").slice(0, 19)}</td>
              <td className="p-3 text-right space-x-2">
                <Button size="sm" variant="outline" onClick={() => setYear(o.academic_year)} data-testid={`history-edit-${o.academic_year}`}>Edit</Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => del(o.academic_year)} data-testid={`history-delete-${o.academic_year}`}>Delete</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==========================================================================
// PHASE 5 — Super Admin sub-components
// ==========================================================================
function AdminOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.adminAnalytics().then(setData).catch(() => toast.error("Failed to load analytics")); }, []);
  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading analytics…</div>;
  const u = data.users, r = data.revenue_paise, e = data.daily_entries;
  const rupees = (p) => `₹${(Number(p || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  return (
    <div className="space-y-4" data-testid="admin-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Total users" value={u.total} testid="ov-total-users"/>
        <MiniStat label="Active" value={u.active} tint="emerald"/>
        <MiniStat label="Premium" value={u.premium} tint="orange" testid="ov-premium"/>
        <MiniStat label="Expired" value={u.expired} tint="slate"/>
        <MiniStat label="New today" value={u.new_today}/>
        <MiniStat label="New this month" value={u.new_this_month}/>
        <MiniStat label="Daily entries · total" value={e.total} tint="emerald"/>
        <MiniStat label="Daily entries · today" value={e.today}/>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Revenue · today" value={rupees(r.today)} tint="orange" testid="ov-rev-today"/>
        <MiniStat label="Revenue · this month" value={rupees(r.this_month)} tint="orange"/>
        <MiniStat label="Revenue · this year" value={rupees(r.this_year)} tint="orange"/>
        <MiniStat label="Revenue · all time" value={rupees(r.all_time)} tint="emerald" testid="ov-rev-all"/>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="chip chip-slate">📈 Registrations · last 30 days</div>
        <div className="mt-3 h-32 flex items-end gap-1">
          {data.registration_trend.map((d) => {
            const max = Math.max(1, ...data.registration_trend.map((x) => x.users));
            const h = (d.users / max) * 100;
            return <div key={d.date} title={`${d.date}: ${d.users}`} className="flex-1 bg-emerald-500/70 rounded-t" style={{ height: `${Math.max(h, 2)}%` }}/>;
          })}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tint, testid }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50" : tint === "orange" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="mt-1 font-display font-extrabold text-2xl text-slate-900">{value}</div>
    </div>
  );
}

function AdminUsers() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // { user, tempPassword }
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.adminUsers({ search: search || undefined, status: status || undefined, limit: 50 });
      setData(r);
    } catch { toast.error("Failed to load users"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const openDetail = async (id) => { setDetail({ loading: true }); try { const d = await api.adminUserDetail(id); setDetail(d); } catch { toast.error("Failed to load"); setDetail(null); } };
  const doAction = async (id, action, days = null, extra = {}) => {
    setBusy(true);
    try {
      const r = await api.adminUserAction(id, { action, days, ...extra });
      toast.success(`User ${action.replace("_", " ")}d`);
      setDetail(await api.adminUserDetail(id));
      load();
      return r;
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const genPassword = () => {
    // Human-friendly temp password: 3 letters + 4 digits (avoids confusable chars)
    const letters = "ABCDEFGHJKMNPQRSTUVWXYZ";
    const digits = "23456789";
    let out = "";
    for (let i = 0; i < 3; i++) out += letters[Math.floor(Math.random() * letters.length)];
    for (let i = 0; i < 4; i++) out += digits[Math.floor(Math.random() * digits.length)];
    return out;
  };
  const startReset = (user) => setResetTarget({ user, tempPassword: genPassword() });
  const confirmReset = async () => {
    if (!resetTarget) return;
    const r = await doAction(resetTarget.user.id, "reset_password", null, { new_password: resetTarget.tempPassword });
    if (r) {
      toast.success("Password reset — share the temp password with the teacher");
    }
  };
  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / mobile / UDISE / school…" className="h-11 flex-1" data-testid="admin-users-search"/>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="admin-users-status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="premium">Premium</option>
          <option value="expired">Expired premium</option>
          <option value="free">Free tier</option>
        </select>
        <Button onClick={load} disabled={loading} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-users-load">{loading ? "…" : "Search"}</Button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-3 text-xs text-slate-500 border-b">Showing {data.items.length} of {data.total}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Name</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Mobile</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">School / UDISE</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Plan</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500" data-testid="admin-users-empty">No users found.</td></tr>}
              {data.items.map((u) => {
                const sub = u.subscription || {};
                const premium = sub.status === "active" && sub.expiry_date && new Date(sub.expiry_date) > new Date();
                return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${u.id}`}>
                    <td className="p-3 font-semibold text-slate-900">{u.full_name || "—"}</td>
                    <td className="p-3">{u.mobile}</td>
                    <td className="p-3 text-slate-600">{u.school_name || "—"}<div className="text-xs text-slate-500">{u.udise || ""}</div></td>
                    <td className="p-3">{premium ? <span className="chip chip-green text-[10px]">Premium</span> : <span className="chip chip-slate text-[10px]">Free</span>}</td>
                    <td className="p-3">{u.is_active !== false ? <span className="text-emerald-700">Active</span> : <span className="text-red-700">Disabled</span>}</td>
                    <td className="p-3 text-right">
                      <div className="flex flex-wrap gap-1 justify-end">
                        <Button size="sm" variant="outline" onClick={() => startReset(u)} data-testid={`admin-user-reset-${u.id}`} className="text-orange-700 border-orange-200 hover:bg-orange-50">
                          <KeyRound size={12}/> Reset
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openDetail(u.id)} data-testid={`admin-user-view-${u.id}`}>View</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {resetTarget && (
        <ResetPasswordDialog
          target={resetTarget}
          busy={busy}
          onRegenerate={() => setResetTarget({ ...resetTarget, tempPassword: genPassword() })}
          onConfirm={confirmReset}
          onClose={() => setResetTarget(null)}
        />
      )}

      {detail && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="admin-user-detail">
          {detail.loading ? <div className="text-sm text-slate-500">Loading…</div> : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <div className="font-display font-bold text-lg text-slate-900">{detail.user.full_name}</div>
                  <div className="text-sm text-slate-600">📱 {detail.user.mobile} · 🏫 {detail.user.school_name || "—"} · UDISE {detail.user.udise || "—"}</div>
                  <div className="text-xs text-slate-500">Created {new Date(detail.user.created_at).toLocaleDateString("en-IN")} · Entries {detail.entries_count}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Close</Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-user-actions">
                <Button size="sm" onClick={() => startReset(detail.user)} disabled={busy} variant="outline" className="text-orange-700 border-orange-200 hover:bg-orange-50" data-testid="admin-action-reset-password">
                  <KeyRound size={12}/> Reset password
                </Button>
                <Button size="sm" onClick={() => doAction(detail.user.id, "extend", 365)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-action-extend-365">+365 days</Button>
                <Button size="sm" onClick={() => doAction(detail.user.id, "extend", 30)} disabled={busy} variant="outline">+30 days</Button>
                <Button size="sm" onClick={() => doAction(detail.user.id, "expire")} disabled={busy} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50">Expire now</Button>
                {detail.user.is_active !== false ? (
                  <Button size="sm" onClick={() => doAction(detail.user.id, "deactivate")} disabled={busy} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50">Deactivate</Button>
                ) : (
                  <Button size="sm" onClick={() => doAction(detail.user.id, "activate")} disabled={busy} variant="outline">Activate</Button>
                )}
              </div>
              <div className="mt-3 text-xs text-slate-600">
                Subscription: {detail.user.subscription?.status || "none"} {detail.user.subscription?.expiry_date && `· expires ${new Date(detail.user.subscription.expiry_date).toLocaleDateString("en-IN")}`}
              </div>
              {detail.payments?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-bold text-slate-500">Recent payments</div>
                  <ul className="mt-1 space-y-1 text-sm">
                    {detail.payments.map((p) => (
                      <li key={p.id} className="flex justify-between border-b border-slate-100 py-1">
                        <span>{p.order_id} · {p.status}</span>
                        <span className="font-semibold">₹{(p.amount / 100).toFixed(0)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function ResetPasswordDialog({ target, busy, onRegenerate, onConfirm, onClose }) {
  const [done, setDone] = useState(false);
  const u = target.user;
  const pwd = target.tempPassword;
  const waMessage = encodeURIComponent(
    `Namaste ${u.full_name || "teacher"}, your PM Poshan Calculator password has been reset.\n\n` +
    `📱 Login mobile: ${u.mobile}\n🔑 New temporary password: ${pwd}\n\n` +
    `Please sign in at https://pmposhancalculator.com/login and change it from Profile → Security → Change password.`
  );
  const waLink = `https://wa.me/91${u.mobile}?text=${waMessage}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pwd);
      toast.success("Temp password copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  const confirm = async () => {
    await onConfirm();
    setDone(true);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="reset-pw-dialog" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-lg bg-orange-500 text-white grid place-items-center shrink-0"><KeyRound size={18}/></span>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg text-slate-900">Reset password</h3>
            <p className="text-sm text-slate-600">For <b>{u.full_name || u.mobile}</b> · 📱 {u.mobile}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Close" data-testid="reset-pw-close">×</button>
        </div>

        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Temporary password</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 font-mono text-2xl font-extrabold tracking-widest text-slate-900 select-all" data-testid="reset-pw-value">{pwd}</code>
            <Button size="sm" variant="outline" onClick={onRegenerate} disabled={done} data-testid="reset-pw-regen" title="Generate another">
              <RotateCw size={14}/>
            </Button>
            <Button size="sm" variant="outline" onClick={copy} data-testid="reset-pw-copy">
              <Copy size={14}/>
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">The teacher must change this from Profile → Security after signing in.</p>
        </div>

        {!done ? (
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="sm:flex-1" data-testid="reset-pw-cancel">Cancel</Button>
            <Button onClick={confirm} disabled={busy} className="sm:flex-1 bg-orange-600 hover:bg-orange-700 text-white" data-testid="reset-pw-confirm">
              {busy ? "Resetting…" : "Reset & set as password"}
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0"/>
              <span>Password reset. Send the temp password to the teacher over WhatsApp.</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="sm:flex-1" data-testid="reset-pw-whatsapp">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <MessageCircle size={16}/> WhatsApp to +91 {u.mobile}
                </Button>
              </a>
              <Button variant="outline" onClick={onClose} className="sm:flex-1" data-testid="reset-pw-done">Done</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminPayments() {
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const load = async () => { try { setData(await api.adminPayments({ status: status || undefined, limit: 100 })); } catch { toast.error("Failed to load"); } };
  useEffect(() => { load(); }, [status]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3" data-testid="admin-payments">
      <div className="flex gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="admin-payments-status">
          <option value="">All</option>
          <option value="paid">Paid</option>
          <option value="created">Pending</option>
          <option value="failed">Failed</option>
          <option value="signature_failed">Signature failed</option>
        </select>
        <Button onClick={load} variant="outline" data-testid="admin-payments-reload">Reload</Button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-3 text-xs text-slate-500 border-b">Showing {data.items.length} of {data.total}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Date</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">User</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Order ID</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Amount</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No payments yet.</td></tr>}
              {data.items.map((p) => (
                <tr key={p.id} className="border-b border-slate-100" data-testid={`admin-payment-${p.id}`}>
                  <td className="p-3 text-slate-600">{new Date(p.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3">{p.user?.full_name || "—"}<div className="text-xs text-slate-500">{p.user?.mobile}</div></td>
                  <td className="p-3 font-mono text-xs">{p.order_id}<div className="text-slate-500">{p.payment_id || ""}</div></td>
                  <td className="p-3 font-semibold">₹{(p.amount / 100).toFixed(0)}</td>
                  <td className="p-3">
                    {p.status === "paid" && <span className="chip chip-green text-[10px]">Paid</span>}
                    {p.status === "created" && <span className="chip chip-slate text-[10px]">Pending</span>}
                    {p.status === "failed" && <span className="chip chip-red text-[10px]">Failed</span>}
                    {p.status === "signature_failed" && <span className="chip chip-red text-[10px]">Sig fail</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AdminAudit() {
  const [data, setData] = useState({ items: [], total: 0 });
  useEffect(() => { api.adminAuditLogs({ limit: 100 }).then(setData).catch(() => toast.error("Failed to load audit")); }, []);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-audit">
      <div className="p-3 text-xs text-slate-500 border-b">Showing {data.items.length} of {data.total} · newest first</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-3 text-xs uppercase font-bold text-slate-500">When</th>
              <th className="p-3 text-xs uppercase font-bold text-slate-500">Admin</th>
              <th className="p-3 text-xs uppercase font-bold text-slate-500">Action</th>
              <th className="p-3 text-xs uppercase font-bold text-slate-500">Target</th>
              <th className="p-3 text-xs uppercase font-bold text-slate-500">IP</th>
              <th className="p-3 text-xs uppercase font-bold text-slate-500">Meta</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No admin actions logged yet.</td></tr>}
            {data.items.map((a) => (
              <tr key={a.id} className="border-b border-slate-100" data-testid={`admin-audit-${a.id}`}>
                <td className="p-3 text-slate-600">{new Date(a.at).toLocaleString("en-IN")}</td>
                <td className="p-3">{a.admin_sub}</td>
                <td className="p-3 font-mono text-xs">{a.action}</td>
                <td className="p-3 font-mono text-xs">{a.target_user_id || "—"}</td>
                <td className="p-3 font-mono text-xs">{a.ip || "—"}</td>
                <td className="p-3 font-mono text-xs">{a.metadata ? JSON.stringify(a.metadata) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Admin → Ads : campaign editor + day-by-day sparkline + placement table
// ============================================================================
function Sparkline({ series, w = 120, h = 30, stroke = "#059669" }) {
  if (!series || series.length === 0) return <svg width={w} height={h}/>;
  const max = Math.max(1, ...series);
  const step = w / Math.max(1, series.length - 1);
  const points = series.map((v, i) => `${i * step},${h - (v / max) * (h - 2) - 1}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible" role="img" aria-label="Daily trend">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {series.map((v, i) => (
        <circle key={i} cx={i * step} cy={h - (v / max) * (h - 2) - 1} r="1.5" fill={stroke}/>
      ))}
    </svg>
  );
}

function AdminAds() {
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // form state {campaign_id, ...}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ads, an] = await Promise.all([api.adminAds(), api.adminAdAnalytics()]);
      setItems(ads.items || []);
      setAnalytics(an);
    } catch { toast.error("Failed to load ads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (c) => setEditing({ ...c, features_json: JSON.stringify(c.features || [], null, 2), placements_json: JSON.stringify(c.placements || {}, null, 2) });
  const save = async () => {
    if (!editing?.campaign_id) return toast.error("Missing campaign_id");
    let features, placements;
    try { features = JSON.parse(editing.features_json || "[]"); }
    catch { return toast.error("Features JSON invalid"); }
    try { placements = JSON.parse(editing.placements_json || "{}"); }
    catch { return toast.error("Placements JSON invalid"); }
    const payload = {
      campaign_id: editing.campaign_id,
      name: editing.name,
      url: editing.url,
      label: editing.label,
      headline: editing.headline,
      subheadline: editing.subheadline,
      description: editing.description,
      features,
      cta: editing.cta,
      trust_line: editing.trust_line,
      image_url: editing.image_url,
      accent: editing.accent,
      active: !!editing.active,
      weight: Number(editing.weight) || 10,
      placements,
      show_to_premium: !!editing.show_to_premium,
    };
    setBusy(true);
    try {
      await api.adminUpsertAd(payload);
      toast.success("Campaign saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (c) => {
    setBusy(true);
    try {
      await api.adminUpsertAd({ campaign_id: c.campaign_id, active: !c.active });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(false); }
  };

  // Build 14-day series per campaign (impressions, clicks)
  const days = analytics?.days || 14;
  const daysList = (() => {
    const list = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now); d.setUTCHours(0,0,0,0); d.setUTCDate(d.getUTCDate() - i);
      list.push(d.toISOString().slice(0, 10));
    }
    return list;
  })();
  const seriesFor = (campaignId, kind) => daysList.map((d) => (analytics?.daily?.[d]?.[campaignId]?.[kind]) || 0);

  return (
    <div className="space-y-4" data-testid="admin-ads">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-slate-900">Promotional Ads</h3>
          <p className="text-sm text-slate-500">Enable/disable campaigns, edit copy, upload creative URL and see daily performance.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-ads-refresh"><RefreshCw size={14}/> Refresh</Button>
      </div>

      {/* Placement performance */}
      {analytics?.placements?.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-ads-placements">
          <div className="px-4 py-2 border-b border-slate-100 text-sm font-bold text-slate-900">Top-performing placements</div>
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="p-3 text-xs uppercase font-bold text-slate-500">Placement</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500 text-right">Impressions</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500 text-right">Clicks</th>
                <th className="p-3 text-xs uppercase font-bold text-slate-500 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {analytics.placements.map((p) => (
                <tr key={p.placement} className="border-t border-slate-100">
                  <td className="p-3 font-semibold">{p.placement}</td>
                  <td className="p-3 text-right">{p.impressions}</td>
                  <td className="p-3 text-right">{p.clicks}</td>
                  <td className="p-3 text-right"><span className="chip chip-green text-[10px]">{p.ctr.toFixed(2)}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Campaign cards */}
      <div className="grid gap-3">
        {items.map((c) => (
          <div key={c.campaign_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-ad-row-${c.campaign_id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {c.image_url && <img src={c.image_url} alt="" className="w-full sm:w-40 h-24 object-cover rounded-lg border border-slate-100"/>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-slate-900">{c.name}</span>
                  <span className={`chip text-[10px] ${c.active ? "chip-green" : "chip-slate"}`}>{c.active ? "Active" : "Off"}</span>
                  <span className="chip chip-slate text-[10px]">weight {c.weight}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{c.headline}</div>
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline">{c.url}</a>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Impr / Clicks / CTR</div>
                <div className="font-display font-bold text-slate-900 text-sm">{c.impressions} · {c.clicks} · {c.impressions ? ((c.clicks/c.impressions*100).toFixed(1)) : "0.0"}%</div>
                <div className="mt-1 flex gap-2 justify-end">
                  <Sparkline series={seriesFor(c.campaign_id, "impression")} stroke="#059669"/>
                  <Sparkline series={seriesFor(c.campaign_id, "click")} stroke="#f97316"/>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  <span className="text-emerald-700">━━ Impr</span> · <span className="text-orange-600">━━ Clicks</span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => toggle(c)} disabled={busy} data-testid={`admin-ad-toggle-${c.campaign_id}`}>
                {c.active ? "Disable" : "Enable"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`admin-ad-edit-${c.campaign_id}`}>Edit</Button>
              <a href={c.url} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="ghost">Open ↗</Button>
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Editor */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="admin-ads-editor" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Edit {editing.campaign_id}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-800 text-xl">×</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <label>Name<Input value={editing.name||""} onChange={(e)=>setEditing({...editing,name:e.target.value})}/></label>
              <label>Destination URL (https)<Input value={editing.url||""} onChange={(e)=>setEditing({...editing,url:e.target.value})} data-testid="admin-ad-edit-url"/></label>
              <label className="sm:col-span-2">Headline<Input value={editing.headline||""} onChange={(e)=>setEditing({...editing,headline:e.target.value})} data-testid="admin-ad-edit-headline"/></label>
              <label className="sm:col-span-2">Subheadline<Input value={editing.subheadline||""} onChange={(e)=>setEditing({...editing,subheadline:e.target.value})}/></label>
              <label className="sm:col-span-2">Description<Input value={editing.description||""} onChange={(e)=>setEditing({...editing,description:e.target.value})}/></label>
              <label>CTA<Input value={editing.cta||""} onChange={(e)=>setEditing({...editing,cta:e.target.value})} data-testid="admin-ad-edit-cta"/></label>
              <label>Trust line<Input value={editing.trust_line||""} onChange={(e)=>setEditing({...editing,trust_line:e.target.value})}/></label>
              <label className="sm:col-span-2">Creative image URL (https)<Input value={editing.image_url||""} onChange={(e)=>setEditing({...editing,image_url:e.target.value})} data-testid="admin-ad-edit-image"/></label>
              {editing.image_url && <img src={editing.image_url} alt="preview" className="sm:col-span-2 w-full max-h-40 object-cover rounded-lg border"/>}
              <label>Accent
                <select value={editing.accent||"emerald"} onChange={(e)=>setEditing({...editing,accent:e.target.value})} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm w-full">
                  <option value="emerald">emerald</option><option value="blue">blue</option><option value="orange">orange</option>
                </select>
              </label>
              <label>Weight (1–100)<Input type="number" min={1} max={100} value={editing.weight||10} onChange={(e)=>setEditing({...editing,weight:Number(e.target.value)})}/></label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.active} onChange={(e)=>setEditing({...editing,active:e.target.checked})}/> Active</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.show_to_premium} onChange={(e)=>setEditing({...editing,show_to_premium:e.target.checked})}/> Show to Premium users</label>
              <label className="sm:col-span-2 text-xs">Features (JSON array of {"{icon, label}"})
                <textarea rows={4} value={editing.features_json} onChange={(e)=>setEditing({...editing,features_json:e.target.value})} className="w-full rounded-md border border-slate-300 bg-white p-2 text-xs font-mono"/>
              </label>
              <label className="sm:col-span-2 text-xs">Placements (JSON object)
                <textarea rows={3} value={editing.placements_json} onChange={(e)=>setEditing({...editing,placements_json:e.target.value})} className="w-full rounded-md border border-slate-300 bg-white p-2 text-xs font-mono"/>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-ad-edit-save">
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ═════════════════════════════════════════════════════════════════════
// Admin: Recharge Requests (60-days-of-rewards workflow)
// ═════════════════════════════════════════════════════════════════════
function AdminRecharge() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // active request being reviewed
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.adminRechargeRequests(statusFilter ? { status: statusFilter } : {});
      setItems(r.items || []);
    } catch { toast.error("Failed to load recharge requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const statusColor = (s) => ({
    REQUESTED: "bg-amber-100 text-amber-800 border-amber-200",
    UNDER_REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
    APPROVED: "bg-blue-100 text-blue-800 border-blue-200",
    PROCESSING: "bg-blue-100 text-blue-800 border-blue-200",
    COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-200",
    REJECTED: "bg-red-100 text-red-800 border-red-200",
    CANCELLED: "bg-slate-100 text-slate-700 border-slate-200",
  }[s] || "bg-slate-100 text-slate-700 border-slate-200");

  const submitEdit = async (nextStatus) => {
    if (!editing) return;
    if (nextStatus === "COMPLETED" && !(editing.transaction_reference || "").trim()) {
      toast.error("Transaction reference is required to mark COMPLETED");
      return;
    }
    setSaving(true);
    try {
      await api.adminRechargeUpdate(editing.id, {
        status: nextStatus,
        transaction_reference: editing.transaction_reference || "",
        admin_notes: editing.admin_notes || "",
      });
      toast.success(`Marked ${nextStatus.replaceAll("_", " ")}`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-recharge">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All statuses</option>
          {["REQUESTED", "UNDER_REVIEW", "APPROVED", "PROCESSING", "COMPLETED", "REJECTED", "CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</Button>
        <div className="text-xs text-slate-500 ml-auto">Total: <b>{items.length}</b></div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="p-3 text-left text-xs uppercase font-bold text-slate-500">User</th>
                <th className="p-3 text-left text-xs uppercase font-bold text-slate-500">Mobile</th>
                <th className="p-3 text-left text-xs uppercase font-bold text-slate-500">Operator</th>
                <th className="p-3 text-right text-xs uppercase font-bold text-slate-500">Successful</th>
                <th className="p-3 text-left text-xs uppercase font-bold text-slate-500">Status</th>
                <th className="p-3 text-left text-xs uppercase font-bold text-slate-500">Requested</th>
                <th className="p-3 text-right text-xs uppercase font-bold text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`admin-recharge-row-${r.id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{r.user?.name || "—"}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.user?.code}</div>
                  </td>
                  <td className="p-3 font-mono">{r.mobile_number}</td>
                  <td className="p-3">{r.operator}{r.circle ? <span className="text-xs text-slate-500"> · {r.circle}</span> : null}</td>
                  <td className="p-3 text-right font-bold">{r.successful_referrals}</td>
                  <td className="p-3"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusColor(r.status)}`}>{r.status}</span></td>
                  <td className="p-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...r })} data-testid={`admin-recharge-review-${r.id}`}>Review</Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">No recharge requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-5" data-testid="admin-recharge-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-slate-900">Recharge Request · {editing.user?.name}</div>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>×</Button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs uppercase text-slate-500 font-bold">Mobile</div><div className="font-mono">{editing.mobile_number}</div></div>
              <div><div className="text-xs uppercase text-slate-500 font-bold">Operator</div><div>{editing.operator}</div></div>
              <div><div className="text-xs uppercase text-slate-500 font-bold">Circle</div><div>{editing.circle || "—"}</div></div>
              <div><div className="text-xs uppercase text-slate-500 font-bold">Plan</div><div>{editing.plan || "—"}</div></div>
              <div><div className="text-xs uppercase text-slate-500 font-bold">Successful referrals</div><div className="font-bold">{editing.successful_referrals}</div></div>
              <div><div className="text-xs uppercase text-slate-500 font-bold">Reward months</div><div>{editing.recharge_months}</div></div>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase text-slate-500 font-bold">Transaction reference (required for COMPLETED)</label>
                <input value={editing.transaction_reference || ""} onChange={(e) => setEditing({ ...editing, transaction_reference: e.target.value })}
                       className="mt-1 w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="admin-recharge-tx"/>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 font-bold">Admin notes</label>
                <textarea value={editing.admin_notes || ""} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })}
                          className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" rows={3} data-testid="admin-recharge-notes"/>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Button variant="outline" onClick={() => submitEdit("UNDER_REVIEW")} disabled={saving}>Under Review</Button>
              <Button variant="outline" onClick={() => submitEdit("APPROVED")} disabled={saving}>Approve</Button>
              <Button variant="outline" onClick={() => submitEdit("PROCESSING")} disabled={saving}>Processing</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => submitEdit("COMPLETED")} disabled={saving} data-testid="admin-recharge-complete">Mark Completed</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => submitEdit("REJECTED")} disabled={saving}>Reject</Button>
              <Button variant="outline" onClick={() => submitEdit("CANCELLED")} disabled={saving}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Admin: Visitor + Funnel Analytics
// ═════════════════════════════════════════════════════════════════════
function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setData(await api.analyticsSummary()); }
    catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-6 text-center text-slate-500">{loading ? "Loading analytics…" : "No data"}</div>;
  const c = data.cards || {};
  const trend = data.trend || [];
  const funnel = data.funnel || {};
  const traffic = data.traffic || {};
  const devices = data.devices || {};

  const Card = ({ icon, label, value, testid }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{icon} {label}</div>
      <div className="mt-1 text-2xl font-display font-black text-slate-900">{value?.toLocaleString?.() ?? value ?? 0}</div>
    </div>
  );

  // 30-day trend (visitors only, mini chart)
  const maxV = Math.max(1, ...trend.map((t) => t.visitors || 0));

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Card icon="👥" label="Total Visitors"    value={c.total_visitors}    testid="ana-total"/>
        <Card icon="🟢" label="Today"             value={c.visitors_today}    testid="ana-today"/>
        <Card icon="📅" label="This Week"         value={c.visitors_week}     testid="ana-week"/>
        <Card icon="📆" label="This Month"        value={c.visitors_month}    testid="ana-month"/>
        <Card icon="👤" label="Registered Users"  value={c.registered_users}  testid="ana-users"/>
        <Card icon="💎" label="Premium Users"     value={c.premium_users}     testid="ana-premium"/>
        <Card icon="📊" label="Sessions"          value={c.sessions}          testid="ana-sessions"/>
        <Card icon="🧮" label="Calculations"      value={c.calculations}      testid="ana-calc"/>
        <Card icon="📄" label="Reports"           value={c.reports}           testid="ana-reports"/>
        <Card icon="📋" label="MDCF Reports"      value={c.mdcf_reports}      testid="ana-mdcf"/>
        <Card icon="📥" label="PDF Downloads"     value={c.pdf_downloads}     testid="ana-pdf"/>
        <Card icon="📲" label="WhatsApp Shares"   value={c.whatsapp_shares}   testid="ana-wa"/>
        <Card icon="🎁" label="Referral Registrations" value={c.referral_registrations} testid="ana-refreg"/>
        <Card icon="💰" label="Referral Conversions"    value={c.referral_conversions}   testid="ana-refconv"/>
        <Card icon="💳" label="Premium Activations"     value={c.premium_activations}    testid="ana-premact"/>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-bold text-slate-900 mb-2">30-day visitor trend</div>
        <div className="flex items-end gap-1 h-32">
          {trend.map((t) => (
            <div key={t.date} title={`${t.date} — visitors ${t.visitors}, regs ${t.registrations}, pay ${t.payments}`}
                 className="flex-1 bg-emerald-500/80 hover:bg-emerald-600 rounded-t"
                 style={{ height: `${Math.max(2, ((t.visitors || 0) / maxV) * 100)}%` }}/>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-slate-500">
          <span>{trend[0]?.date}</span><span>{trend[trend.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold text-slate-900 mb-3">🎯 Referral Funnel</div>
          <div className="space-y-2 text-sm">
            {[
              ["Link Shared", funnel.shared || 0],
              ["Link Opened", funnel.link_opened || 0],
              ["Registered", funnel.registered || 0],
              ["Premium Activated", funnel.activated || 0],
              ["Successful Referral", funnel.successful || 0],
            ].map(([lbl, v], i, arr) => {
              const top = arr[0][1] || 1;
              const pct = Math.round((v / top) * 100);
              return (
                <div key={lbl}>
                  <div className="flex justify-between text-xs"><span>{lbl}</span><span className="font-bold">{v}</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-1">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-emerald-600" style={{ width: `${pct}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold text-slate-900 mb-3">🚦 Traffic & Devices</div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="uppercase tracking-widest font-bold text-slate-500 mb-1">Sources</div>
              {Object.entries(traffic).map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5"><span>{k}</span><span className="font-bold">{v}</span></div>
              ))}
            </div>
            <div>
              <div className="uppercase tracking-widest font-bold text-slate-500 mb-1">Devices</div>
              {Object.entries(devices).map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5"><span>{k}</span><span className="font-bold">{v}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Admin: Campaign Configuration (Refer & Earn knobs — no redeploy)
// ═════════════════════════════════════════════════════════════════════
function AdminCampaign() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const c = await api.adminCampaignConfig();
      setForm({
        campaign_active: !!c.campaign_active,
        milestone: c.milestone ?? 50,
        reward_days: c.reward_days ?? 365,
        recharge_months: c.recharge_months ?? 6,
        popup_enabled: !!c.popup_enabled,
        popup_cooldown_hours: c.popup_cooldown_hours ?? 24,
        popup_headline: c.popup_headline || "",
        popup_subline: c.popup_subline || "",
        share_message: c.share_message || "",
        campaign_starts_at: c.campaign_starts_at ? new Date(c.campaign_starts_at).toISOString().slice(0, 16) : "",
        campaign_ends_at: c.campaign_ends_at ? new Date(c.campaign_ends_at).toISOString().slice(0, 16) : "",
      });
    } catch { toast.error("Failed to load campaign config"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.share_message.includes("{link}")) {
      toast.error("Share message must contain a {link} placeholder");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        campaign_active: !!form.campaign_active,
        milestone: Number(form.milestone) || 50,
        reward_days: Number(form.reward_days) || 365,
        recharge_months: Number(form.recharge_months) || 0,
        popup_enabled: !!form.popup_enabled,
        popup_cooldown_hours: Number(form.popup_cooldown_hours) || 0,
        popup_headline: form.popup_headline || null,
        popup_subline: form.popup_subline || null,
        share_message: form.share_message,
        campaign_starts_at: form.campaign_starts_at ? new Date(form.campaign_starts_at).toISOString() : null,
        campaign_ends_at: form.campaign_ends_at ? new Date(form.campaign_ends_at).toISOString() : null,
      };
      await api.adminCampaignSave(payload);
      toast.success("Campaign config saved — live for all users");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!form) return <div className="p-6 text-center text-slate-500">{loading ? "Loading campaign config…" : "No config"}</div>;

  return (
    <div className="space-y-6" data-testid="admin-campaign">
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">Master switch</div>
          <div className="mt-1 text-sm text-emerald-900">
            {form.campaign_active ? "🟢 Campaign is LIVE — new rewards can be earned." : "🔴 Campaign is OFF — no new rewards will be granted."}
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.campaign_active} onChange={(e) => set("campaign_active", e.target.checked)}
                 className="h-5 w-5" data-testid="camp-active"/>
          <span className="text-sm font-semibold">Active</span>
        </label>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="font-display font-bold text-slate-900">🎯 Reward Rules</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Milestone (successful referrals)</label>
            <input type="number" min="1" value={form.milestone} onChange={(e) => set("milestone", e.target.value)}
                   className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-milestone"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Premium reward (days)</label>
            <input type="number" min="1" value={form.reward_days} onChange={(e) => set("reward_days", e.target.value)}
                   className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-days"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Recharge reward (months, 0 = off)</label>
            <input type="number" min="0" value={form.recharge_months} onChange={(e) => set("recharge_months", e.target.value)}
                   className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-recharge-months"/>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">🪟 Login Popup</div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.popup_enabled} onChange={(e) => set("popup_enabled", e.target.checked)}
                   className="h-5 w-5" data-testid="camp-popup-enabled"/>
            <span className="text-sm font-semibold">Show on login</span>
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Cooldown (hours)</label>
            <input type="number" min="0" value={form.popup_cooldown_hours} onChange={(e) => set("popup_cooldown_hours", e.target.value)}
                   className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-cooldown"/>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Headline</label>
          <input value={form.popup_headline} onChange={(e) => set("popup_headline", e.target.value)}
                 className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-headline"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Sub-headline</label>
          <input value={form.popup_subline} onChange={(e) => set("popup_subline", e.target.value)}
                 className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-subline"/>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="font-display font-bold text-slate-900">💬 WhatsApp share message</div>
        <p className="text-xs text-slate-500">Use <code className="bg-slate-100 px-1 rounded">{"{link}"}</code> where you want the user's referral link inserted. The message will be sent verbatim.</p>
        <textarea value={form.share_message} onChange={(e) => set("share_message", e.target.value)} rows={14}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-mono" data-testid="camp-share-msg"/>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="font-display font-bold text-slate-900">📅 Campaign window (optional)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Starts at</label>
            <input type="datetime-local" value={form.campaign_starts_at} onChange={(e) => set("campaign_starts_at", e.target.value)}
                   className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-start"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Ends at</label>
            <input type="datetime-local" value={form.campaign_ends_at} onChange={(e) => set("campaign_ends_at", e.target.value)}
                   className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="camp-end"/>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={load} disabled={loading}>Reload</Button>
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="camp-save">
          {saving ? "Saving…" : "Save Campaign Config"}
        </Button>
      </div>
    </div>
  );
}

